import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import './DailySuperMeter.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DAILY_SUPER_PITCH = 'THE DAILY SUPER JACKPOT WILL HIT TODAY at a random time';
export const DAILY_SUPER_SUB = 'Stay active and playing for your chance!';
const SLOT_PATHS = ['/casino/lucky-ducky', '/casino/lucky-flamingo', '/casino/buffalo-bucks', '/casino/balls-of-fire',
    '/casino/thunder-cash', '/casino/panda-paradise', '/casino/outlaws-loot', '/casino/btpb', '/casino/keno-4-hearts',
    '/casino/keno-super-double', '/casino/blackjack', '/casino/blackjack-v2', '/casino/three-card-poker', '/casino/craps',
    '/craps', '/casino/roulette', '/roulette'];
// Canvas games: chip spot in each game's design space (centre origin, y down), next to its jackpot meters.
const CANVAS = {
    '/casino/lucky-ducky': { P: { W: 1000, H: 1740, x: 0, y: -464, w: 420 }, L: { W: 1920, H: 1140, x: 0, y: -255, w: 330 } },
    '/casino/balls-of-fire': { P: { W: 600, H: 1050, k: 0.97, x: 0, y: -353, w: 200 }, L: { W: 1600, H: 1000, k: 0.97, x: -464, y: -361, w: 260 } },
    '/casino/lucky-flamingo': { P: { W: 1080, H: 1920, x: 185, y: -375, w: 400 }, L: { W: 1920, H: 1080, x: 780, y: -285, w: 290 } },
    '/casino/btpb': { P: { W: 600, H: 1000, x: 0, y: -215, w: 240 }, L: { W: 1680, H: 1060, x: 179, y: -195, w: 300 } },
};
// Keno / table pages have no jackpot strip: pin the chip just under the page header (left = 'c' for centred).
const PINNED = {
    '/casino/keno-4-hearts': { top: 118, left: 'c' }, '/casino/keno-super-double': { top: 6, left: 92, landscape: true },
    '/casino/blackjack': { top: 64, left: 'c' }, '/casino/blackjack-v2': { top: 64, left: 'c' },
    '/casino/three-card-poker': { top: 58, left: 'c' }, '/casino/craps': { top: 58, left: 'c' }, '/craps': { top: 58, left: 'c' },
    '/casino/roulette': { top: 64, left: 12 }, '/roulette': { top: 64, left: 12 },
};
const CHIP_W = 170;
const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const useDailySuper = (enabled = true) => {
    const [d, setD] = useState(null);
    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        const load = () => axios.get(`${API}/jackpots/daily-super`).then(({ data }) => { if (alive) setD(data); }).catch(() => {});
        load();
        const iv = setInterval(load, 8000);
        return () => { alive = false; clearInterval(iv); };
    }, [enabled]);
    return d?.active ? d : null;
};

const Flames = () => <div className="ds-flames" aria-hidden="true">{Array.from({ length: 9 }, (_, i) => <span key={i} className="ds-flame" />)}</div>;

const ChipBody = ({ d }) => (
    <>
        {d.hot && <Flames />}
        <span className="ds-label">Daily Super</span>
        <span className="ds-amount" data-testid="daily-super-meter-amount">{fmt(d.amount)}</span>
        {d.hot && <span className="ds-hot-tag" data-testid="daily-super-meter-hot">ABOUT TO HIT</span>}
    </>
);

// In-game chip for DOM-built slots: drop it inside the jackpot strip (parent must be position: relative).
export const DailySuperChip = ({ style }) => {
    const d = useDailySuper();
    if (!d) return null;
    return <div className={`ds-chip ${d.hot ? 'ds-hot' : ''}`} style={style} data-testid="daily-super-meter"><ChipBody d={d} /></div>;
};

const usePlaced = (cfg) => {
    const [pos, setPos] = useState(null);
    useEffect(() => {
        if (!cfg) { setPos(null); return undefined; }
        const calc = () => {
            const vw = window.innerWidth, vh = window.innerHeight, c = vh > vw ? cfg.P : cfg.L;
            const s = Math.min(vw / c.W, vh / c.H) * (c.k || 1);
            setPos({ left: vw / 2 + c.x * s, top: vh / 2 + c.y * s, scale: (c.w * s) / CHIP_W });
        };
        calc();
        window.addEventListener('resize', calc);
        return () => window.removeEventListener('resize', calc);
    }, [cfg]);
    return pos;
};

const IDLE_CALL = /\/(state|config|refresh|session|jackpots|login|me|balance|manifest[^/]*)(\?|$)/;
// A game API call that isn't an idle/status poll means the player has started playing (spin, start, ack...).
const playedIn = (win) => { if (!win.__dsBuf) { win.performance.setResourceTimingBufferSize?.(5000); win.__dsBuf = true; } return playedEntries(win); };
const playedEntries = (win) => win.performance.getEntriesByType('resource').some((e) => (e.initiatorType === 'fetch' || e.initiatorType === 'xmlhttprequest')
    && e.name.includes('/api/') && !e.name.includes('/api/auth/') && !e.name.includes('/api/jackpots/') && !IDLE_CALL.test(new URL(e.name).pathname));

// Chip counts as on screen when it is the top element at its centre (loading / rotate screens sit above it).
const chipVisible = () => {
    const el = document.querySelector('.ds-chip');
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (!r.width) return false;
    el.style.pointerEvents = 'auto';
    const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
    el.style.pointerEvents = '';
    return !!hit && el.contains(hit);
};

// Shows only once the player is really in the game (first spin / jackpot strip on screen), never on loading screens.
const useStarted = (pathname, canvas) => {
    const [started, setStarted] = useState(false);
    useEffect(() => {
        setStarted(false);
        let done = false;
        const go = () => { done = true; setStarted(true); };
        const tick = () => {
            if (done) return;
            if (canvas) document.querySelectorAll('iframe').forEach((fr) => { try { if (fr.contentWindow && playedIn(fr.contentWindow)) go(); } catch { /* cross-origin */ } });
            else if (chipVisible()) go();
        };
        tick();
        const iv = setInterval(tick, 500);
        return () => clearInterval(iv);
    }, [pathname, canvas]);
    return started;
};

export const DailySuperMeter = () => {
    const { pathname } = useLocation();
    const onSlot = SLOT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`)) && !pathname.endsWith('/sim');
    const cfg = CANVAS[pathname.replace(/\/kiosk$/, '')] || null;
    const d = useDailySuper(onSlot);
    const pos = usePlaced(onSlot ? cfg : null);
    const pinCfg = PINNED[pathname] || null;
    const pin = pinCfg && (!pinCfg.landscape || window.innerWidth > window.innerHeight) ? pinCfg : null;
    const started = useStarted(pathname, !!cfg || pathname.startsWith('/casino/buffalo-bucks')) || !!pinCfg;
    const [announced, setAnnounced] = useState(false);
    useEffect(() => { setAnnounced(false); }, [pathname]);
    useEffect(() => {
        if (!started || announced) return undefined;
        const t = setTimeout(() => setAnnounced(true), 8000);
        return () => clearTimeout(t);
    }, [started, announced]);
    if (!onSlot || !d || !started) return null;
    return (
        <>
            {!announced && (
                <div className="ds-announce" onClick={() => setAnnounced(true)} role="status" data-testid="daily-super-announce">
                    <div className="ds-announce-title">{DAILY_SUPER_PITCH}</div>
                    <div className="ds-announce-sub">{DAILY_SUPER_SUB}</div>
                </div>
            )}
            {pin && (
                <div className={`ds-chip ds-pinned ${d.hot ? 'ds-hot' : ''}`} data-testid="daily-super-meter"
                    style={pin.left === 'c' ? { top: pin.top, left: '50%', transform: 'translateX(-50%)' } : { top: pin.top, left: pin.left, transform: 'none' }}>
                    <ChipBody d={d} />
                </div>
            )}
            {pos && (
                <div className={`ds-meter ${d.hot ? 'ds-hot' : ''}`} data-testid="daily-super-meter"
                    style={{ left: pos.left, top: pos.top, transform: `translate(-50%, -50%) scale(${pos.scale})` }}>
                    <ChipBody d={d} />
                </div>
            )}
        </>
    );
};

export default DailySuperMeter;
